<script setup>
// NOTICE: KAPTION-INTEGRATION — note-cards.RENDER ("F · Pill")
// A card is one line: the outcome, its time and a badge whose color is the tone. Opening it
// shows the why, the facts and the trace. Everything the agent did is outlined in the copilot
// violet whatever the outcome; the tone only colors the badge inside (note-cards.RENDER.3/4).
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useKaptionNoteCardActions } from 'dashboard/composables/useKaptionNoteCardActions';
import {
  QUIET_NOTE_CARD_KINDS,
  PROSE_NOTE_CARD_KINDS,
  NOTE_CARD_AGENT_EDGE,
  noteCardTone,
  noteCardBadgeClass,
  noteCardBadgeIcon,
} from 'dashboard/helper/kaptionNoteCards';
import { useMessageContext } from './provider.js';

const props = defineProps({
  noteCard: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const { id, conversationId, createdAt } = useMessageContext();
const { openDashboardApp } = useKaptionNoteCardActions();

const AGENT_EDGE = NOTE_CARD_AGENT_EDGE;
const QUIET_EDGE =
  'border border-[rgba(124,58,237,0.35)] dark:border-[rgba(167,139,250,0.40)]';

const isOpen = ref(false);
const isSubmitting = ref(false);
const toggle = () => {
  isOpen.value = !isOpen.value;
};

const card = computed(() => props.noteCard);
const time = computed(() => messageTimestamp(createdAt.value, 'h:mm a'));

// ── the quieter lines: the assistant's explanation and its takeover (note-cards.RENDER.5)
const isQuiet = computed(() => QUIET_NOTE_CARD_KINDS.includes(card.value.kind));
const isTakeover = computed(() => card.value.kind === 'takeover');
const quietText = computed(() =>
  isTakeover.value
    ? t('CONVERSATION.NOTE_CARDS.PILL.TAKEOVER', { name: card.value.title })
    : card.value.title
);
const fullText = computed(() =>
  (card.value.trace.length ? card.value.trace : [card.value.summary])
    .filter(Boolean)
    .join('\n')
);
const canOpenQuiet = computed(() => !isTakeover.value && !!fullText.value);

// ── the tone, on the badge (note-cards.RENDER.4)
const isPending = computed(
  () => card.value.type !== 'info' && card.value.state === 'pending'
);
const isResolved = computed(() =>
  ['completed', 'confirmed', 'cancelled'].includes(card.value.state)
);
const tone = computed(() => noteCardTone(card.value));
const badgeClass = computed(() => noteCardBadgeClass(tone.value));
const badgeIcon = computed(() => noteCardBadgeIcon(card.value, tone.value));
const titleClass = computed(() =>
  tone.value === 'danger' ? 'text-n-ruby-11' : 'text-n-slate-12'
);

// ── the open card
const isProse = computed(() => PROSE_NOTE_CARD_KINDS.includes(card.value.kind));
const traceCaption = computed(() => {
  const label =
    card.value.traceLabel || t('CONVERSATION.NOTE_CARDS.PILL.TRACE');
  const count = card.value.trace.length;
  const steps =
    count === 1
      ? t('CONVERSATION.NOTE_CARDS.PILL.STEP_ONE')
      : t('CONVERSATION.NOTE_CARDS.PILL.STEP_MANY', { count });
  return `${label} · ${steps}`;
});
const resolutionText = computed(() => {
  const resolution = card.value.resolution;
  if (!resolution) return '';
  return [resolution.label, resolution.by?.name].filter(Boolean).join(' · ');
});
const footer = computed(() =>
  [card.value.actorName, card.value.source].filter(Boolean).join(' · ')
);

// ── note-cards.ACTIONS.1 / note-cards.ACTIONS.4
const buttons = computed(() => {
  if (!isPending.value) return [];
  const { actions } = card.value;
  if (card.value.type === 'action') {
    return [{ key: 'primary', label: actions.primary?.label, primary: true }];
  }
  return [
    { key: 'confirm', label: actions.confirm?.label, primary: true },
    { key: 'cancel', label: actions.cancel?.label, primary: false },
  ];
});

// The producer's data, as it sent it: messages reach the view camelCased, so a dashboard app
// gets the stored card's own keys.
const producerData = () => {
  const raw = store.getters.getSelectedChat?.messages?.find(
    message => message.id === id.value
  );
  return raw?.content_attributes?.note_card?.data || card.value.data || {};
};

const resolve = async choice => {
  if (!isPending.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    if (card.value.appId) {
      openDashboardApp(card.value.appId, {
        cardId: card.value.id,
        action: choice,
        conversationId: conversationId.value,
        data: producerData(),
      });
      return;
    }
    const { data } = await MessageApi.noteCardAction(
      conversationId.value,
      id.value,
      choice
    );
    store.dispatch('updateMessage', data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONVERSATION.NOTE_CARDS.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex w-full flex-col items-center" data-test="note-card-pill">
    <template v-if="isQuiet">
      <button
        v-if="!isOpen"
        type="button"
        class="inline-flex max-w-full items-center gap-1.5 rounded-full bg-n-solid-1 px-3 py-0.5 text-xs leading-5 text-n-slate-11 disabled:cursor-default"
        :class="QUIET_EDGE"
        :disabled="!canOpenQuiet"
        :aria-expanded="canOpenQuiet ? 'false' : undefined"
        data-test="note-card-quiet"
        @click="toggle"
      >
        <span
          class="i-woot-copilot-sparkles size-3 shrink-0 text-kpt-copilot"
        />
        <span class="min-w-0 truncate">{{ quietText }}</span>
        <span class="shrink-0 text-n-slate-10">{{ time }}</span>
        <span
          v-if="canOpenQuiet"
          class="i-lucide-chevron-down size-3 shrink-0"
        />
      </button>
      <div
        v-else
        class="note-card-open flex w-full max-w-lg items-start gap-2 rounded-2xl bg-n-solid-1 py-2 pe-2 ps-3 text-xs leading-[18px] text-n-slate-11"
        :class="QUIET_EDGE"
        data-test="note-card-quiet-open"
      >
        <span
          class="i-woot-copilot-sparkles mt-0.5 size-3 shrink-0 text-kpt-copilot"
        />
        <p class="mb-0 min-w-0 flex-1 whitespace-pre-wrap break-words">
          {{ fullText }}
        </p>
        <button
          type="button"
          class="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
          aria-expanded="true"
          :aria-label="t('CONVERSATION.NOTE_CARDS.PILL.CLOSE')"
          @click="toggle"
        >
          <span class="i-lucide-chevron-up size-3" />
        </button>
      </div>
    </template>

    <template v-else>
      <button
        v-if="!isOpen"
        type="button"
        class="inline-flex max-w-full items-center gap-2 rounded-full bg-n-solid-1 py-1 pe-2.5 ps-1 text-[13px] leading-5 text-n-slate-12"
        :class="AGENT_EDGE"
        aria-expanded="false"
        data-test="note-card-pill-closed"
        @click="toggle"
      >
        <span
          class="inline-flex size-5 shrink-0 items-center justify-center rounded-full"
          :class="badgeClass"
          data-test="note-card-badge"
        >
          <span class="size-3" :class="badgeIcon" />
        </span>
        <span class="min-w-0 truncate font-semibold" :class="titleClass">
          {{ card.title }}
        </span>
        <span
          v-if="isPending"
          class="shrink-0 rounded-full bg-[#FFF7C2] px-1.5 text-[11px] font-semibold leading-[18px] text-[#AB6400] dark:bg-[#302008] dark:text-[#FFCA16]"
          data-test="note-card-pending"
        >
          {{ t('CONVERSATION.NOTE_CARDS.PILL.PENDING') }}
        </span>
        <span class="shrink-0 text-n-slate-10">{{ time }}</span>
        <span class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-11" />
      </button>

      <div
        v-else
        class="note-card-open flex w-full max-w-lg flex-col gap-2 rounded-2xl bg-n-solid-1 px-3.5 pb-3 pt-2.5"
        :class="AGENT_EDGE"
        data-test="note-card-pill-open"
      >
        <div class="flex items-center gap-2">
          <span
            class="inline-flex size-5 shrink-0 items-center justify-center rounded-full"
            :class="badgeClass"
          >
            <span class="size-3" :class="badgeIcon" />
          </span>
          <span
            class="min-w-0 flex-1 text-sm font-semibold leading-5"
            :class="titleClass"
          >
            {{ card.title }}
          </span>
          <span class="shrink-0 text-xs text-n-slate-10">{{ time }}</span>
          <button
            type="button"
            class="-me-1.5 inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            aria-expanded="true"
            :aria-label="t('CONVERSATION.NOTE_CARDS.PILL.CLOSE')"
            data-test="note-card-close"
            @click="toggle"
          >
            <span class="i-lucide-chevron-up size-3.5" />
          </button>
        </div>

        <p
          v-if="isResolved && resolutionText"
          class="mb-0 flex items-center gap-1.5 text-[13px] leading-[18px]"
          :class="
            card.state === 'cancelled' ? 'text-n-slate-11' : 'text-n-teal-11'
          "
          data-test="note-card-resolution"
        >
          <span
            class="size-3.5 shrink-0"
            :class="
              card.state === 'cancelled' ? 'i-lucide-x' : 'i-lucide-check'
            "
          />
          {{ resolutionText }}
        </p>

        <p
          v-if="card.summary"
          class="mb-0 break-words text-sm leading-5 text-n-slate-11"
          data-test="note-card-summary"
        >
          {{ card.summary }}
        </p>

        <div
          v-if="card.facts.length"
          class="flex flex-wrap gap-1.5"
          data-test="note-card-facts"
        >
          <span
            v-for="fact in card.facts"
            :key="fact.label"
            class="inline-flex items-baseline gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs leading-[18px]"
          >
            <span class="text-n-slate-10">{{ fact.label }}</span>
            <span class="break-words text-n-slate-12">{{ fact.value }}</span>
          </span>
        </div>

        <div
          v-if="card.trace.length"
          class="flex flex-col gap-1.5"
          data-test="note-card-trace"
        >
          <p
            v-if="isProse"
            class="mb-0 whitespace-pre-wrap break-words text-sm leading-5 text-n-slate-11"
          >
            {{ card.trace.join('\n') }}
          </p>
          <template v-else>
            <span
              class="text-[10.5px] font-semibold uppercase leading-[14px] tracking-[0.12em] text-n-slate-10"
            >
              {{ traceCaption }}
            </span>
            <ol
              class="m-0 flex list-none flex-col gap-0.5 rounded-lg bg-n-alpha-2 px-2.5 py-2 font-mono text-xs leading-[18px] text-n-slate-11"
            >
              <li
                v-for="(line, index) in card.trace"
                :key="index"
                class="flex gap-2"
              >
                <span class="min-w-2.5 text-end text-n-slate-10">
                  {{ index + 1 }}
                </span>
                <span class="min-w-0 break-words">{{ line }}</span>
              </li>
            </ol>
          </template>
        </div>

        <div
          v-if="buttons.length"
          class="flex flex-wrap items-center gap-2"
          data-test="note-card-actions"
        >
          <button
            v-for="button in buttons"
            :key="button.key"
            type="button"
            class="inline-flex min-h-8 items-center rounded-lg px-3 text-[13px] font-semibold leading-5 transition disabled:cursor-not-allowed disabled:opacity-60"
            :class="
              button.primary
                ? 'bg-kpt-copilot text-white hover:opacity-90'
                : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
            "
            :disabled="isSubmitting"
            :data-test="`note-card-action-${button.key}`"
            @click="resolve(button.key)"
          >
            {{ button.label }}
          </button>
        </div>

        <span
          v-if="footer"
          class="text-xs leading-4 text-n-slate-10"
          data-test="note-card-footer"
        >
          {{ footer }}
        </span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.note-card-open {
  animation: note-card-open 160ms ease-out;
  transform-origin: top center;
}

@keyframes note-card-open {
  from {
    opacity: 0;
    transform: scale(0.97) translateY(-2px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .note-card-open {
    animation: none;
  }
}
</style>
